/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global Office */
import { DialogMessageEvent, PodioSettings } from '../types/types';
import { EmailService } from '../services/emailService';
import { PodioService } from '../services/PodioService';
import { SettingsService } from '../services/SettingsService';

Office.initialize = () => {
  console.log("Office.initialize called");
};

Office.onReady(() => {
  // Office.js is ready
});

async function action(event: Office.AddinCommands.Event) {
  try {

    /* Commenting out dialog-related code for now
    const credentials = await SettingsService.getCredentials();
    if (!SettingsService.isValidCredentials(credentials)) {
      Office.context.ui.displayDialogAsync(
        "https://outlookplugin.cmshelp.dk/dialog.html",
        { height: 50, width: 30, promptBeforeOpen: false },
        (asyncResult: Office.AsyncResult<Office.Dialog>) => {
          if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
            console.log("Successfully opened dialog");
            const dialog = asyncResult.value;
            
            dialog.addEventHandler(
              Office.EventType.DialogMessageReceived,
              (arg: DialogMessageEvent) => async () => {
                console.log("Dialog message received", arg);

                try {
                  const settings: PodioSettings = JSON.parse(arg.message);
                  if (settings.status === "success") {
                    SettingsService.saveSettings(settings);
                    dialog.close();
                    
                    const appId = 30198490;
                    PodioService.setCredentials(settings.clientId, settings.clientSecret);

                    const emailDetails = await EmailService.getEmailDetails();
                    console.log("Email details:", emailDetails);
                    const emailBlob = await EmailService.createEMLFile();
                    await PodioService.authenticateAndCreateItem(appId, settings.appToken, emailDetails, emailBlob);
                  }
                } catch (error) {
                  console.error("Error parsing dialog message:", error);
                }
              }
            );
          }
        }
      );
    } else {
    */
    // Hardcoded credentials for testing
    const credentials = {
      clientId: "backupt1",
      clientSecret: "jKrn3Zw4HLnlWMawwVm0WBwCP1yblz7LZCpxRIlJ9luKBrHrIYDp2Mm2KlhMneYU",
      appToken: "02cfd1cd8ce7a297dc07d417b6c9d4e5"
    };

    console.log("Using hardcoded credentials");
    const appId = 30198490;
    PodioService.setCredentials(credentials.clientId, credentials.clientSecret);

    const emailDetails = await EmailService.getEmailDetails();
    console.log("Email details:", emailDetails);
    const emailBlob = await EmailService.createEMLFile();
    const podioItem = await PodioService.authenticateAndCreateItem(appId, credentials.appToken, emailDetails, emailBlob);
    
    // Show success notification with appropriate message based on create/update
    const successMessage = podioItem.wasUpdated 
      ? `Item already exists and has been successfully updated in Podio!✓ `
      : `Email successfully sent to Podio!✓`;

    // Show notification
    Office.context.mailbox.item.notificationMessages.replaceAsync(
      "successNotification", 
      {
        type: "informationalMessage",
        message: successMessage,
        icon: "Icon.16x16",
        persistent: false
      }
    );

    
    event.completed();
  } catch (error) {
    console.error("Error in action:", error);
    
    // Show error notification
    Office.context.mailbox.item.notificationMessages.replaceAsync(
      "errorNotification",
      {
        type: "errorMessage",
        message: "Failed to send email to Podio. Please try again.",
        persistent: false
      }
    );
    
    event.completed();
  }
}

async function openInPodio(event: Office.AddinCommands.Event) {
  try {
    // Hardcoded credentials for testing
    const credentials = {
      clientId: "backupt1",
      clientSecret: "jKrn3Zw4HLnlWMawwVm0WBwCP1yblz7LZCpxRIlJ9luKBrHrIYDp2Mm2KlhMneYU",
      appToken: "02cfd1cd8ce7a297dc07d417b6c9d4e5"
    };

    console.log("Opening item in Podio");
    const appId = 30198490;
    PodioService.setCredentials(credentials.clientId, credentials.clientSecret);

    // Get email details to extract internetMessageId
    const emailDetails = await EmailService.getEmailDetails();
    
    if (!emailDetails?.internetMessageId) {
      Office.context.mailbox.item.notificationMessages.replaceAsync(
        "errorNotification",
        {
          type: "errorMessage",
          message: "Unable to find email identifier. Please send to Podio first.",
          persistent: false
        }
      );
      event.completed();
      return;
    }

    // Normalize the internetMessageId
    const normalizedMessageId = PodioService.normalizeInternetMessageId(emailDetails.internetMessageId);
    
    if (!normalizedMessageId) {
      Office.context.mailbox.item.notificationMessages.replaceAsync(
        "errorNotification",
        {
          type: "errorMessage",
          message: "Unable to process email identifier. Please send to Podio first.",
          persistent: false
        }
      );
      event.completed();
      return;
    }

    // Get the item link from Podio
    const itemLink = await PodioService.getItemLinkByExternalId(appId, credentials.appToken, normalizedMessageId);

    if (!itemLink) {
      // Item not found in Podio
      Office.context.mailbox.item.notificationMessages.replaceAsync(
        "errorNotification",
        {
          type: "errorMessage",
          message: "Item not available in Podio. Please send to Podio first.",
          persistent: false
        }
      );
      event.completed();
      return;
    }

    // Show notification before opening
    Office.context.mailbox.item.notificationMessages.replaceAsync(
      "successNotification",
      {
        type: "informationalMessage",
        message: "Opening Podio item in browser...",
        icon: "Icon.16x16",
        persistent: false
      }
    );

    // Open the link in browser
    try {
      if (Office.context.ui && typeof Office.context.ui.openBrowserWindow === 'function') {
        Office.context.ui.openBrowserWindow(itemLink);
      } else {
        window.open(itemLink, '_blank');
      }
      
      // Show notification after opening
      Office.context.mailbox.item.notificationMessages.replaceAsync(
        "successNotification",
        {
          type: "informationalMessage",
          message: "Opened Podio item in browser✓",
          icon: "Icon.16x16",
          persistent: false
        }
      );
    } catch (error) {
      console.log("Using window.open fallback:", error);
      window.open(itemLink, '_blank');
    }
    
    event.completed();
  } catch (error) {
    console.error("Error opening item in Podio:", error);
    
    Office.context.mailbox.item.notificationMessages.replaceAsync(
      "errorNotification",
      {
        type: "errorMessage",
        message: "Failed to open item in Podio. Please try again.",
        persistent: false
      }
    );
    
    event.completed();
  }
}

// Register the functions with Office
Office.actions.associate("action", action);
Office.actions.associate("openInPodio", openInPodio);
